import { EventEmitter, Listener } from "src/util/event-emitter";
import { scatter, slideFade } from "src/fx/object/sprite-fx";
import BaseScene from "src/scenes/base";

interface ScrollingColorPickerOpts {
    direction?: 'left' | 'right',
    padding?: number,
    buttonWidth?: number,
    speed?: number,
    y?: number | string,
    stackBeneath?: ScrollingColorPicker<BaseScene> | null
}

class ScrollingColorPicker<T extends BaseScene> {
    
    private events: EventEmitter = new EventEmitter();
    private scene: BaseScene;
    public wrapRect: Phaser.Geom.Rectangle;
    private colors: chroma.Color[];
    private buttons: Phaser.GameObjects.Arc[];
    private buttonWidth: number = 0;
    //private step: number = 1.5;
    private opts: Required<ScrollingColorPickerOpts>;

    constructor(scene: T, colors: chroma.Color[], opts: ScrollingColorPickerOpts = {}){
        this.scene = scene;
        this.colors = [...colors];
        this.opts = {
            stackBeneath: null,
            speed: .5,
            direction: 'left',
            padding: 0,
            buttonWidth: 30,
            y: '50%',
            ...opts
        };

        this.buttonWidth = this.opts.buttonWidth;

        /**
         * Calculate our step based on the "speed" value in opts. The speed value provided is a value between 0-1 which 
         * is multiplied against a (max) percentage of the total screen width. This is necessary to normalize the scrolling
         * speed across different screen widths.
         */
        // let stepDirectionMultiplier = this.opts.direction === 'left' ? -1 : 1; //Step sign to determine direction
        // let maxStepRatio = .005; //Max percentage of scene width allowed for step
        // let stepRatio = this.opts.speed * maxStepRatio; //Our actual step ratio is a percentage of our max percentage
        // this.step = stepDirectionMultiplier * (scene.cameras.main.width * stepRatio);

        /**
         * Width of the wrap container must be the exact width of each of its items including padding between them
         */
        let wrapContainerWidth = ((this.buttonWidth * 2) + this.opts.padding) * colors.length;

        let y = 0;
        if (this.opts.stackBeneath){
            y = this.opts.stackBeneath.wrapRect.bottom;
        } else {
            y = typeof this.opts.y === 'string' ? (parseInt(this.opts.y) / 100) * scene.cameras.main.height : this.opts.y;
            y -= this.opts.buttonWidth;
        }

        /**
         * Create wrap rectangle at given 
         */
        this.wrapRect = new Phaser.Geom.Rectangle(0, y, wrapContainerWidth, 100);
        this.wrapRect.centerX = scene.cameras.main.width / 2; 
    
        /**
         * Generate buttons
         */
        this.buttons = this.colors.map((color, i) => {
            let x = ((this.buttonWidth * 2) * i) + (this.opts.padding * (i + 1))//, y = 0;
            let circle = this.scene.add.circle(x, y, this.buttonWidth, color.num());
                circle.setInteractive();
            return circle;
        })

        this.scene.input.on('gameobjectdown', this.handlePointerDown, this);
        this.scene.registerUpdate(this);
    }

    private handlePointerDown(pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Arc){
        let index = this.buttons.indexOf(gameObject);
        if (index !== -1){
            this.events.emit('select', this.colors[index]);
            this.scene.events.removeListener('gameobjectdown', this.handlePointerDown, this);
        }
    }

    public update(time: number, delta: number){
        let stepDirectionMultiplier = this.opts.direction === 'left' ? -1 : 1; //Step sign to determine direction
        let maxStepRatio = .4; //Max percentage of scene width allowed for step
        let stepRatio = this.opts.speed * maxStepRatio; //Our actual step ratio is a percentage of our max percentage
        let deltaStep = stepDirectionMultiplier * (stepRatio * delta);

        Phaser.Actions.IncX(this.buttons, deltaStep, 0);
        Phaser.Actions.WrapInRectangle(this.buttons, this.wrapRect);
    }

    public enter(delay: number = 0){
        let originalSpeed = this.opts.speed;
        this.opts.speed = 4;
        return Promise.all([
            new Promise((res, rej) => {
                this.scene.tweens.add({
                    ease: Phaser.Math.Easing.Linear,
                    targets: this.opts,
                    speed: originalSpeed,
                    duration: 1000, 
                    delay: delay,
                    onComplete: () => res(null)
                });
            }),
            new Promise((res, rej) => {
                this.buttons.forEach(b => b.alpha = 0);
                this.scene.tweens.add({
                    ease: Phaser.Math.Easing.Linear,
                    targets: this.buttons,
                    alpha: 1,
                    duration: 1000, 
                    delay: delay,
                    onComplete: () => res(null)
                });
            })
        ])
    }

    public leave(){
        this.scene.unregisterUpdate(this);
        return scatter(this.buttons);
    }

    public destroy(){
        this.scene.unregisterUpdate(this);
        this.scene.events.removeListener('gameobjectdown', this.handlePointerDown, this);
        this.buttons.forEach(b => b.destroy());
        this.colors = [];
        this.events.removeAllListeners();
    }

    public stop(){
        this.scene.unregisterUpdate(this);
    }

    public start(){
        this.scene.registerUpdate(this);
    }

    public on(event: string, listener: Listener){
        this.events.on(event, listener);
    }

    public removeListener(event: string, listener: Listener){
        this.events.removeListener(event, listener);
    }
}

export {
    ScrollingColorPicker
}